import { useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { debounce } from '@mui/material/utils';
import { getComparator } from 'pages/configurations/helpers';

const useSearchAndSort = ({ generateStringForSearch, initialList, defaultSortFn }) => {
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState('default');
  const [sortBy, setSortBy] = useState(null);
  const [rows, setRows] = useState([]);

  // Store the full list with default sorting applied
  const fullListRef = useRef([]);

  // Apply default sort to initial list
  const sortedInitialList = useMemo(() => {
    return defaultSortFn(initialList);
  }, [initialList, defaultSortFn]);

  // Apply sorting to a list based on current sort state
  const applySorting = useCallback(
    (list) => {
      if (sort === 'default') {
        return defaultSortFn(list);
      }
      const comparator = getComparator(sort, sortBy);
      return list.slice().sort(comparator);
    },
    [sort, sortBy, defaultSortFn],
  );

  // Apply search filter to a list
  const applySearch = useCallback(
    (list) => {
      if (!searchText) {
        return list;
      }

      return list.filter((item) => {
        const string = generateStringForSearch(item);
        return string.toLowerCase().includes(searchText.toLowerCase().trimStart());
      });
    },
    [searchText, generateStringForSearch],
  );

  // Main effect: process initial list and apply all filters/sorting
  useEffect(() => {
    // Store the sorted initial list
    fullListRef.current = sortedInitialList;

    // Apply sorting first (to the full list)
    const sortedList = applySorting(sortedInitialList);

    // Then apply search filter
    const filteredList = applySearch(sortedList);

    setRows(filteredList);
  }, [sortedInitialList, applySorting, applySearch]);

  // Handle search text changes
  const onChangeSearch = useCallback((text) => {
    setSearchText(text);
  }, []);

  const debounceOnChangeSearch = useMemo(() => debounce(onChangeSearch, 250), [onChangeSearch]);

  // Handle sort changes
  const handleChangeSort = useCallback(
    (columnID) => {
      if (sortBy === columnID) {
        switch (sort) {
          case 'asc':
            setSort('desc');
            break;
          case 'desc':
            setSort('default');
            break;
          default:
            setSort('asc');
        }
      } else {
        setSort('asc');
        setSortBy(columnID);
      }
    },
    [sortBy, sort],
  );

  return {
    handleChangeSort,
    debounceOnChangeSearch,
    rows,
    searchText,
    sort,
    sortBy,
  };
};

export default useSearchAndSort;
